import { useOutletContext, useNavigate, useLocation } from "react-router-dom";
import { WebActivityMatch, WebActivityOutletCtx } from "../../webInterface";
import { useCallback, useEffect, useState } from "react";
import { Card, Space, Select, Table, Spin, Checkbox, Empty } from "antd";
import { getWebActivityMatches } from "../../webBridge";
import Bracket from "./components/bracket";

const WebActivityCategoies: React.FC = () => {
    const data = useOutletContext() as WebActivityOutletCtx;
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const urlCategoryId = searchParams.get('category_id');

    const requestedCategoryId = urlCategoryId ? parseInt(urlCategoryId) : 0;
    const initialCategoryId = data.categories.some(category => category.id === requestedCategoryId)
        ? requestedCategoryId
        : data.categories[0]?.id || 0;
    const [categoryId, setCategoryId] = useState<number>(initialCategoryId);
    const [matches, setMatches] = useState<WebActivityMatch[]>([]);
    const [selectedRound, setSelectedRound] = useState<number>(-2);
    const [loadingState, setLoadingState] = useState(false);
    const [hideLockedMatches, setHideLockedMatches] = useState(false);

    const handleCategoryChange = (newCategoryId: number) => {
        setLoadingState(true);
        setMatches([]);
        setSelectedRound(-2);
        setHideLockedMatches(false);
        setCategoryId(newCategoryId);

        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.set('category_id', newCategoryId.toString());
        navigate(`${location.pathname}?${newSearchParams.toString()}`);
    };

    const loadMatches = useCallback(async() => {
        try {
            const res = await getWebActivityMatches(data.activity.id, categoryId);
            if (!res.ok) {
                setMatches([]);
                return;
            }
            const resp = await res.json() as { items?: WebActivityMatch[] };
            setMatches(Array.isArray(resp.items) ? resp.items : []);
        } finally {
            setLoadingState(false);
        }
    }, [data.activity.id, categoryId]);

    useEffect(() => {
        if (categoryId) {
            setLoadingState(true);
            loadMatches();
        } else {
            setMatches([]);
            setLoadingState(false);
        }
    }, [categoryId, loadMatches]);

    const handleRoundChange = (value: number) => {
        setSelectedRound(value);
    };

    const filteredMatches = selectedRound === -2 
        ? matches 
        : matches.filter(match => match.round === selectedRound);

    const displayMatches = hideLockedMatches
        ? filteredMatches.filter(match => match.state !== 'locked')
        : filteredMatches;

    const currentCategory = data.categories.find(cat => cat.id === categoryId);
    const roundInfo = currentCategory?.rounds || [];

    const columns = [
        {
            title: 'Round',
            dataIndex: 'round',
            key: 'round',
            render: (round: number) => {
                const roundInfo = currentCategory?.rounds.find(r => r.round === round);
                return roundInfo?.name || `Round ${round}`;
            }
        },
        {
            title: 'Bout',
            dataIndex: 'bout_number',
            key: 'bout_number',
        },
        {
            title: 'Plan Date',
            dataIndex: 'plan_date',
            key: 'plan_date',
        },
        {
            title: 'Player 1',
            dataIndex: ['participants', 0, 'display_name'],
            key: 'player1',
        },
        {
            title: 'Player 2',
            dataIndex: ['participants', 1, 'display_name'],
            key: 'player2',
        },
        {
            title: 'Status',
            dataIndex: 'state',
            key: 'state',
        },
        {
            title: 'Winner',
            dataIndex: 'winner_id',
            key: 'winner',
            render: (winnerId: string, record: WebActivityMatch) => {
                const winner = record.participants.find(p => p.id === winnerId);
                return winner?.display_name || '-';
            }
        }
    ];

    const inGameMatches = matches.filter(match => match.state === 'in_game');

    if (data.categories.length === 0) {
        return <Empty description="No categories available" />;
    }

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Card>
                <Space wrap>
                    {data.categories.map((category) => (
                        <Card
                            key={category.id}
                            size="small"
                            style={{
                                cursor: 'pointer',
                                backgroundColor: categoryId === category.id ? '#1890ff' : '#fff',
                                color: categoryId === category.id ? '#fff' : '#000',
                            }}
                            onClick={() => handleCategoryChange(category.id)}
                        >
                            {category.name} 
                        </Card>
                    ))}
                </Space>
            </Card>
            
            {inGameMatches.length > 0 && (
                <Card title="Live Matches" style={{ backgroundColor: '#fffbe6' }}>
                    <Table 
                        dataSource={inGameMatches} 
                        columns={columns} 
                        rowKey="id"
                        pagination={false}
                        size="small"
                    />
                </Card>
            )}

            <div style={{ width: '100%', overflow: 'auto', minHeight: '500px' }}>
                {loadingState ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <Bracket matches={matches} roundNames={currentCategory?.round_names} category={currentCategory} />
                )}
            </div>

            <Card>
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Space>
                        <Select
                            style={{ width: 200 }}
                            value={selectedRound}
                            onChange={handleRoundChange}
                            options={[
                                { label: 'All Rounds', value: -2 },
                                ...roundInfo.map((round) => ({
                                    label: round.name,
                                    value: round.round
                                }))
                            ]}
                        />
                        <Checkbox
                            checked={hideLockedMatches}
                            onChange={(e) => setHideLockedMatches(e.target.checked)}
                        >
                            Hide Locked Matches
                        </Checkbox>
                    </Space>
                    
                    <Table 
                        dataSource={displayMatches} 
                        columns={columns} 
                        rowKey="id"
                        pagination={false}
                        loading={loadingState}
                    />
                </Space>
            </Card>

        </Space>
    );
};

export default WebActivityCategoies;
